import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse } from 'qs'
import { Box, Tooltip } from '@mui/material'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

import { TableUrlPagination } from '../../../../ui'
import { APPS_TABLE_COLUMNS, APPS_TABLE_ACTIONS, DEFAULT_QUERY } from '../../constants'
import { useSupervisionContext } from '../../contexts'
import { supervisionDataPreparation } from '../../logics'
import { useCustomCombo } from '../../../../features/combos'
import { TableActions } from '../table-actions'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const AppsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const combos = useSelector((state) => state.combos)
  useCustomCombo(['gestores'])
  const location = useLocation()
  const {
    state: {
      data,
      pagination: { total_solicitudes: totalEntries },
      loading,
      search,
      msLoaded,
    },
    supervisionSelect,
    supervisionTableCheck,
    supervisionLoad,
  } = useSupervisionContext()

  useEffect(() => {
    supervisionTableCheck([])
  }, [])

  useEffect(() => {
    const urlSearchParams = parse(location.search.split('?')[1])
    const searchParams = {
      ...DEFAULT_QUERY,
      ...urlSearchParams,
    }
    supervisionLoad(searchParams)
  }, [location.search, supervisionLoad])

  const handleSelectRows = useCallback(
    (rows) => {
      supervisionTableCheck(rows)
    },
    [supervisionTableCheck]
  )

  const formattedColumns = useMemo(
    () =>
      [...APPS_TABLE_COLUMNS, ...APPS_TABLE_ACTIONS].map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.supervision.table.columns.${column.field}.title` }),
          size: column.width,
          minSize: column.width,
          maxSize: column.width,
          enableSorting: column.sorting !== false,
          muiTableBodyCellProps: {
            sx: {
              width: `${column.width}px`,
              minWidth: `${column.width}px`,
              maxWidth: `${column.width}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          muiTableHeadCellProps: {
            sx: {
              width: `${column.width}px`,
              minWidth: `${column.width}px`,
              maxWidth: `${column.width}px`,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflow: 'visible',
              textOverflow: 'clip',
            },
          },
        }

        if (column.field === 'view') {
          return {
            ...baseColumn,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ row }) => <TableActions rowData={row.original} />,
            muiTableBodyCellProps: {
              align: 'center',
              sx: {
                ...baseColumn.muiTableBodyCellProps.sx,
              },
            },
          }
        }

        if (column.field === 'alerta') {
          return {
            ...column,
            accessorKey: column.field,
            header: '',
            Header: () => (
              <Tooltip
                title={formatMessage({ id: 'pages.supervision.table.columns.alerta.title' })}
              >
                <NotificationsNoneIcon sx={{ fontSize: 20 }} />
              </Tooltip>
            ),
            Cell: ({ row }) =>
              row.original.alerta ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <NotificationsActiveIcon color="secondary" sx={{ fontSize: 20 }} />
                </Box>
              ) : null,
            muiTableHeadCellProps: {
              align: 'center',
              sx: {
                width: `${column.size || 50}px`,
                minWidth: `${column.size || 50}px`,
                maxWidth: `${column.size || 50}px`,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'visible',
                textOverflow: 'clip',
              },
            },
            muiTableBodyCellProps: {
              align: 'center',
              sx: {
                padding: '0px',
                width: `${column.size || 50}px`,
                minWidth: `${column.size || 50}px`,
                maxWidth: `${column.size || 50}px`,
                overflow: 'hidden',
              },
            },
          }
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return <span>{lookup[value] ?? value ?? '--'}</span>
            },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(
    () => supervisionDataPreparation(data, { intl }, combos),
    [data, intl, combos]
  )

  // Override global header styles to prevent ellipsis
  const columnsWithHeaderOverride = useMemo(
    () =>
      formattedColumns.map((column) => ({
        ...column,
        muiTableHeadCellProps: {
          ...column.muiTableHeadCellProps,
          sx: (theme) => ({
            ...(typeof column.muiTableHeadCellProps?.sx === 'function'
              ? column.muiTableHeadCellProps.sx(theme)
              : column.muiTableHeadCellProps?.sx),
            overflow: 'visible !important',
            textOverflow: 'clip !important',
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
            '& *': {
              overflow: 'visible !important',
              textOverflow: 'clip !important',
            },
          }),
        },
      })),
    [formattedColumns]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      tableLayout: 'fixed',
      draggable: false,
      initialState: {
        columnPinning: {
          left: ['mrt-row-select', 'alerta', 'solicitud_id'],
        },
      },
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      supervisionSelect(row.id, row.certificateType, row.docType)
    },
    [supervisionSelect]
  )

  return (
    <TableUrlPagination
      columns={columnsWithHeaderOverride}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
      resetSelectionTrigger={msLoaded || data}
    />
  )
}
